import moment from 'moment';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { generatePath } from 'react-router-dom';

import {
  blockedCustomersApi,
  blockedCustomersHistoryApi,
  merchantsApi,
  shopsApi,
} from 'api';
import {
  CloseDialogResult,
  CopyText,
  CrudPage,
  DataGridColumnDefinition,
  dataGridColumns,
  Dialog,
} from 'components';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import {
  useMutation,
  usePartialQuery,
  useQueryFilters,
  useShopsQuery,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  BlockedCustomer,
  BlockedCustomerHistory,
  BlockedCustomersFilters,
} from 'types';
import { blockedCustomersUtils, formatUtils } from 'utils';

type Props = {
  archived?: boolean;
};

export const BlockedCustomersList: React.FC<Props> = ({ archived }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.blocked_customers',
  });
  const { t: tCommon } = useTranslation();
  const { role, isAdmin, isTechOperator } = useUser();
  const queryClient = useQueryClient();

  useQueryFilters<BlockedCustomersFilters>({
    from: moment().subtract(1, 'w').startOf('d').toISOString(),
  });

  const queryFn = useMemo(() => {
    if (archived) {
      return blockedCustomersHistoryApi.getAllPaginated;
    }
    return blockedCustomersApi.getAllPaginated;
  }, [archived]);

  const queryResult = usePartialQuery(QueryKey.BlockedCustomers, queryFn);

  const [confirmRemoveDialogProps, setConfirmRemoveDialogProps] = useState<{
    open: boolean;
    data?: BlockedCustomer;
  }>({
    open: false,
  });

  const queryResultMerchants = useQuery(
    QueryKey.Merchants,
    merchantsApi.getAllAsRole(role),
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const { mutate: removeBlockedCustomer } = useMutation(
    blockedCustomersApi.remove,
    {
      onSuccess: () => {
        setConfirmRemoveDialogProps({ open: false });
        queryClient.invalidateQueries(QueryKey.BlockedCustomers);
      },
    },
  );

  const detailsUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.BLOCKED_CUSTOMERS_DETAILS;
    }
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.BLOCKED_CUSTOMERS_DETAILS;
    }
    return undefined;
  }, [isAdmin, isTechOperator]);

  const getEditLink = useCallback(
    (data: BlockedCustomer) => ({
      to: generatePath(detailsUrl!, { id: data.id }),
    }),
    [detailsUrl],
  );

  const handleCloseRemoveDialog = useCallback(
    ({ ok, data }: CloseDialogResult<BlockedCustomer>) => {
      if (ok && data) {
        removeBlockedCustomer(data.id);
      } else {
        setConfirmRemoveDialogProps({ open: false });
      }
    },
    [removeBlockedCustomer],
  );

  const handleRemove = useCallback((data: BlockedCustomer) => {
    setConfirmRemoveDialogProps({ open: true, data });
  }, []);

  const columns = useMemo(
    (): DataGridColumnDefinition<
      BlockedCustomer | BlockedCustomerHistory
    >[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.shop'),
        valueGetter: (item) => (
          <Fragment>
            <div>{item.merchant?.user?.name}</div>
            <div>{formatUtils.formatName(item.shop)}</div>
          </Fragment>
        ),
      },
      {
        header: t('fields.customers'),
        multiValueRenderer: {
          itemsGetter: (item) => item.customers ?? [],
          valueGetter: ({
            customerId,
            customerIp,
            customerFingerprint,
          }: {
            customerId: string;
            customerIp: string;
            customerFingerprint: string;
          }) => (
            <div className="tw-mb-2" key={customerId || customerIp}>
              {customerId && (
                <div>
                  <CopyText text={customerId} />
                </div>
              )}
              {customerIp && (
                <div>
                  <CopyText text={customerIp} />
                </div>
              )}
              {customerFingerprint && (
                <div>
                  <CopyText text={customerFingerprint} />
                </div>
              )}
            </div>
          ),
        },
      },
      {
        header: t('fields.rules'),
        valueGetter: (item) => (
          <Fragment>
            <div>{`${t('fields.duration')}: ${
              !!item.duration
                ? `${item.duration} ${tCommon('suffixes.minutes')}`
                : t('fields.permanent')
            }`}</div>
            {!!item.count && <div>{`${t('fields.count')}: ${item.count}`}</div>}
            {!!item.period && (
              <div>{`${t('fields.period')}: ${item.period} ${tCommon(
                'suffixes.minutes',
              )}`}</div>
            )}
          </Fragment>
        ),
      },
      {
        header: t('fields.until_at'),
        valueKey: 'untilAt',
        valueFormatter: (value) =>
          value ? formatUtils.formatDate(value) : '-',
        hidden: archived,
      },
      {
        header: t('fields.block_at'),
        valueKey: 'blockAt' as any,
        valueFormatter: formatUtils.formatDate,
        hidden: !archived,
      },
      {
        header: t('fields.processed_at'),
        valueKey: 'processedAt' as any,
        valueFormatter: formatUtils.formatDate,
        hidden: !archived,
      },
      {
        header: t('fields.processor'),
        valueGetter: (item) => (item as BlockedCustomerHistory).processor?.name,
        hidden: !archived,
      },
      dataGridColumns.getActionsColumn({
        handleRemove,
        getEditLink,
        hidden: archived,
      }),
    ],
    [t, tCommon, handleRemove, getEditLink, archived],
  );

  const filtersDefinitions = useMemo(
    () =>
      blockedCustomersUtils.getCommonFilters(
        queryResultMerchants.data,
        queryResultShops.data,
      ),
    [queryResultMerchants.data, queryResultShops.data],
  );

  return (
    <Fragment>
      <CrudPage
        filters={{ filtersDefinitions, withCommon: true }}
        table={{ queryResult, columns, paginated: true }}
      >
        <Dialog
          title={t('remove_dialog.title')}
          onClose={handleCloseRemoveDialog}
          {...confirmRemoveDialogProps}
        >
          <div className="tw-mb-4">
            {t('remove_dialog.description', {
              count: confirmRemoveDialogProps.data?.customers?.length,
            })}
          </div>
          {confirmRemoveDialogProps.data && (
            <Fragment>
              <div className="tw-mb-2">
                <div>{`${t('fields.shop')}:`}</div>
                <div>{confirmRemoveDialogProps.data.merchant.user?.name}</div>
                <div>
                  {formatUtils.formatName(confirmRemoveDialogProps.data.shop)}
                </div>
              </div>
              <div>{`${t('remove_dialog.until_at')}: ${
                confirmRemoveDialogProps.data.untilAt
                  ? formatUtils.formatDate(
                      confirmRemoveDialogProps.data.untilAt,
                    )
                  : '-'
              }`}</div>
            </Fragment>
          )}
        </Dialog>
      </CrudPage>
    </Fragment>
  );
};
